import { Component, inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Person, PersonService } from '../../../../services/person.service';
import { InputComponent } from '../input/input.component';
import { CreatePersonDialogComponent } from './create-person-dialog/create-person-dialog.component';

@Component({
    selector: 'app-person-input',
    standalone: false,
    templateUrl: './person-input.component.html',
    styleUrl: './person-input.component.scss',
})
export class PersonInputComponent extends InputComponent implements OnInit {
    private personService = inject(PersonService);
    private dialog = inject(MatDialog);

    fcContactTelephone = this.formData.form.controls.planningData.get('contacttelefoon');
    fcCustomerId = this.formData.form.controls.planning.get('afas_customer_id');

    persons: Person[] = [];

    get selectedPersonPhoneNumber(): string | null {
        const person = this.persons.find(p => p.id === this.fc.value);
        return person?.phone_number ?? null;
    }

    get hasCustomerSelected(): boolean {
        return !!this.fcCustomerId?.value;
    }

    ngOnInit() {
        this.loadPersons(this.fcCustomerId?.value);

        this.fcCustomerId?.valueChanges.subscribe(value => {
            const selectedPerson = this.persons.find(p => p.id === this.fc.value);
            const personBelongsToCustomer = !!value && selectedPerson && selectedPerson?.afas_customer_id === value;

            if (this.fc.value && !personBelongsToCustomer && selectedPerson) {
                this.fc.setValue(null);
                this.fc.markAsDirty();
                this.fcContactTelephone?.setValue(null);
                this.fcContactTelephone?.markAsDirty();
            }

            this.loadPersons(value);
        });
    }

    loadPersons(customerId: string | number | null | undefined) {
        if (!customerId) {
            this.persons = [];
            return;
        }

        this.personService.getAll(customerId).subscribe(persons => {
            this.persons = this.sortPersonsByPhonenumber(persons);

            if (this.persons.length === 1 && this.fc.value !== this.persons[0].id) {
                this.fc.setValue(this.persons[0].id);
                this.fc.markAsDirty();
                this.populateContactTelephone(this.persons[0]);
            }
        });
    }

    sortPersonsByPhonenumber(persons: Person[]): Person[] {
        return [...persons].sort((a, b) => Number(!a.phone_number) - Number(!b.phone_number));
    }

    onSelection(personId: number) {
        const person = this.persons.find(p => p.id === personId);
        if (person) {
            this.populateContactTelephone(person);
        }
    }

    populateContactTelephone(person: Person) {
        this.fcContactTelephone?.setValue(person.phone_number);
        this.fcContactTelephone?.markAsDirty();
    }

    openCreatePersonDialog(searchTerm?: string) {
        const customerId = this.fcCustomerId?.value;
        if (!customerId) {
            return;
        }

        const dialogRef = this.dialog.open(CreatePersonDialogComponent, {
            data: { afas_customer_id: customerId, last_name: searchTerm },
            minWidth: '500px',
            maxWidth: '95vw',
            disableClose: true,
        });

        dialogRef.afterClosed().subscribe((person: Person | undefined) => {
            if (person) {
                this.persons = this.sortPersonsByPhonenumber([...this.persons, person]);
                this.fc.setValue(person.id);
                this.fc.markAsDirty();
                this.populateContactTelephone(person);
            }
        });
    }

    openEditPersonDialog() {
        const customerId = this.fcCustomerId?.value;
        const person = this.persons.find(p => p.id === this.fc.value);
        if (!customerId || !person) {
            return;
        }

        const dialogRef = this.dialog.open(CreatePersonDialogComponent, {
            data: { afas_customer_id: customerId, person },
            minWidth: '500px',
            maxWidth: '95vw',
            disableClose: true,
        });

        dialogRef.afterClosed().subscribe((updatedPerson: Person | undefined) => {
            if (updatedPerson) {
                const index = this.persons.findIndex(p => p.id === updatedPerson.id);
                if (index !== -1) {
                    const persons = [...this.persons];
                    persons[index] = updatedPerson;
                    this.persons = this.sortPersonsByPhonenumber(persons);
                }
                this.populateContactTelephone(updatedPerson);
                this.fc.markAsDirty();
            }
        });
    }
}
